/**
 * Auth0 SPA configuration, parsed from the worktree-root `.env` file.
 *
 * Vite only exposes vars prefixed with `VITE_` to client code, so the .env
 * file re-exports the SPA-relevant values as `VITE_AUTH0_*` from the bare
 * `AUTH0_*` vars used by the C++ backend. This keeps a single source of truth
 * while making it impossible for unrelated `AUTH0_*` vars (or anything else)
 * to accidentally end up in the browser bundle.
 *
 * Returns `null` when the tenant is not configured. Callers should treat
 * `null` as "auth disabled in this environment" and surface a clear UI
 * affordance (Sign-in disabled with an explanatory tooltip) rather than
 * crashing.
 */
export interface Auth0Config {
  domain: string;
  clientId: string;
  audience: string;
  scope: string;
}

export function loadAuth0Config(env: ImportMetaEnv = import.meta.env): Auth0Config | null {
  const domain = env.VITE_AUTH0_DOMAIN?.trim();
  const clientId = env.VITE_AUTH0_FRONTEND_CLIENT_ID?.trim();
  const audience = env.VITE_AUTH0_AUDIENCE?.trim();
  const scope = env.VITE_AUTH0_SCOPE?.trim() || 'openid profile email';

  if (!domain || !clientId || !audience) {
    return null;
  }

  return { domain, clientId, audience, scope };
}

export const isAuthConfigured = (env: ImportMetaEnv = import.meta.env): boolean =>
  loadAuth0Config(env) !== null;
